import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { LeftOutlined } from "@ant-design/icons";
import { LanguageChange } from "shared/components";
import "./NewQuiz.scss";
import { useCallback, useEffect, useState } from "react";
import SpotifySearch from "./SpotifySearch";
import { Form } from "antd";
import InstagramSearch from "./InstagramSearch";
import YoutubeSearch from "./YoutubeSearch";
import ListField from "./ListField";
import { useSelector } from "react-redux";
import { GenresState } from "explorer/reducers/generos";
import DefaultField from "./DefaultField";
import { useTranslation } from "react-i18next";
import FreeAssessmentTheme from "shared/themes/FreeAssessmentTheme";
import { fetchUserIpLocation } from "shared/service/StrmService";
import { QUIZ_INFO } from "shared/constants/StorageConsts";
import { isMobile } from "react-device-detect";
import PhoneWithDDD from "shared/components/phone-with-ddd/PhoneWithDDD";
import { validatePhone } from "shared/helpers/phone-helpers";

enum Step {
  Platform = 0,
  AboutMusic = 1,
  AboutYou = 2,
}

enum FieldItem {
  Spotify = "SPOTIFY",
  Instagram = "INSTAGRAM",
  Youtube = "YOUTUBE",
  Genre = "GENRE",
  Country = "COUNTRY",
  Phone = "PHONE",
}

const fieldStepMap = {
  [Step.Platform]: [FieldItem.Spotify, FieldItem.Instagram, FieldItem.Youtube],
  [Step.AboutMusic]: [FieldItem.Genre],
  [Step.AboutYou]: [FieldItem.Country, FieldItem.Phone],
};

const fieldOrder = Object.values(FieldItem);

const stepTitles = {
  [Step.Platform]: "Plataforms",
  [Step.AboutMusic]: "Music",
  [Step.AboutYou]: "You",
};

type UserLocation = {
  city: string;
  country: string;
  countryCode: string;
  state: string;
};

const NewQuiz: React.FC<any> = (props) => {
  const { history } = props;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(Step.Platform);
  const [userLocation, setUserLocation] = useState<UserLocation>();
  const [field, setField] = useState<FieldItem>(FieldItem.Spotify);
  const { genreOptions } = useSelector((state: GenresState) => state.genres);
  const { countryOptions } = useSelector((state: any) => state.countries);

  const getUserLocation = useCallback(async () => {
    const response: any = await fetchUserIpLocation();
    if (response) {
      const { countryCode }: UserLocation = response?.response || {};
      setUserLocation(response?.response);
      form.setFieldValue("countryCode", countryCode);
      form.setFieldValue("countryCodePhone", countryCode);
    }
  }, [form]);

  const saveFreeAssessment = async () => {
    const { spotify, ...fields } = form.getFieldsValue(true);

    const params = {
      name: spotify?.name,
      genreCodes: [fields?.genre],
      ...fields,
      spotify,
    };

    try {
      sessionStorage.setItem(
        QUIZ_INFO,
        JSON.stringify({
          ...params,
        })
      );

      history.push("/cadastre", { quiz: true });
    } catch (error) {
      console.error(error);
    }
  };

  const onClickBack = () => {
    const currentIndex = Object.values(FieldItem).indexOf(field);
    if (currentIndex > 0) {
      setField(fieldOrder[currentIndex - 1]);
      if (!fieldStepMap[step].includes(fieldOrder[currentIndex - 1])) {
        setStep(step - 1);
      }
    }
  };

  const onClickNext = () => {
    const currentIndex = Object.values(FieldItem).indexOf(field);
    if (currentIndex < fieldOrder.length - 1) {
      setField(fieldOrder[currentIndex + 1]);
      if (!fieldStepMap[step].includes(fieldOrder[currentIndex + 1])) {
        setStep(step + 1);
      }
    } else {
      saveFreeAssessment();
    }
  };

  const handleFormChange = (changedValues: any) => {
    if (changedValues.countryCode) {
      form.setFieldsValue({
        countryCodePhone: changedValues.countryCode,
      });
    }
  };

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  return (
    <div className="free-assessment">
      <FreeAssessmentTheme>
        <div className="header">
          <div className="back-button">
            {field !== FieldItem.Spotify && (
              <LeftOutlined onClick={onClickBack} />
            )}
          </div>
          {!isMobile && (
            <div className="logo-container">
              <Logo className="logo" />
            </div>
          )}

          <div className="quiz-content">
            <h2>{t(`quiz.pages.FreeAssessment.${stepTitles[step]}`)}</h2>
            <span className="progress-indicator">
              {[...Array(3)].map((_, index) => (
                <span key={index} className={index <= step ? "active" : ""} />
              ))}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              order: 2,
            }}
          >
            <LanguageChange.SelectLanguageChange />
          </div>
        </div>

        <Form form={form} onValuesChange={handleFormChange}>
          <div className="body">
            <div className="content">
              {field === FieldItem.Spotify && (
                <Form.Item name="spotify" noStyle>
                  <SpotifySearch
                    onClickNext={onClickNext}
                    countryCode={userLocation?.countryCode}
                  />
                </Form.Item>
              )}

              {field === FieldItem.Instagram && (
                <Form.Item name="instagram" noStyle>
                  <InstagramSearch onClickNext={onClickNext} />
                </Form.Item>
              )}

              {field === FieldItem.Youtube && (
                <Form.Item name="youtube" noStyle>
                  <YoutubeSearch onClickNext={onClickNext} />
                </Form.Item>
              )}

              {field === FieldItem.Genre && (
                <Form.Item name="genre" noStyle>
                  <ListField
                    placeholder={t("quiz.pages.FreeAssessment.searchGenre")}
                    options={genreOptions}
                    onClickNext={onClickNext}
                    label={t("quiz.pages.FreeAssessment.searchGenre")}
                    name="genre"
                  />
                </Form.Item>
              )}

              {field === FieldItem.Country && (
                <Form.Item name="countryCode" noStyle>
                  <ListField
                    placeholder={t("quiz.pages.FreeAssessment.countryCode")}
                    options={countryOptions}
                    onClickNext={onClickNext}
                    label={t("quiz.pages.FreeAssessment.countrySearch")}
                    name="countryCode"
                  />
                </Form.Item>
              )}

              {field === FieldItem.Phone && (
                <Form.Item name="phone" noStyle>
                  <DefaultField
                    onClickNext={onClickNext}
                    name="phone"
                    inputComponent={
                      <PhoneWithDDD
                        names={{
                          ddi: "ddi",
                          phoneCountry: "countryCodePhone",
                          phone: "phone",
                        }}
                        rules={{
                          ddi: [
                            {
                              required: true,
                              message: t("quiz.pages.Questionario.dddRequired"),
                            },
                          ],
                          phone: [
                            {
                              required: true,
                              message: t(
                                "quiz.pages.Questionario.phoneRequired"
                              ),
                            },
                            {
                              validator(_: any, value: any) {
                                const { countryCodePhone } =
                                  form.getFieldsValue();
                                return validatePhone(
                                  countryCodePhone,
                                  value,
                                  t("pages.MeusDados.telefoneInvalido")
                                );
                              },
                            },
                          ],
                        }}
                      />
                    }
                    label={t("quiz.pages.FreeAssessment.genreSearch")}
                  />
                </Form.Item>
              )}
            </div>
          </div>
        </Form>
      </FreeAssessmentTheme>
    </div>
  );
};

export default NewQuiz;
